
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useI18n } from "src/utils/lni18n";
import { useDialog } from "src/components/Modalservice/Dialogservice";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import courseService from "./CourseService";
import { allPartsAtom } from "src/coursepart/CoursePartCollectionService";
import { InputDialog, InputOptions } from "src/components/Modalservice/InputDialog";
import { TinyTheme } from "./domain/CourseEditModel";
import { debouncer } from "src/utils/debouncer";
import { ConfirmationButtons, ConfirmationDialog, ConfirmationOptions } from "src/components/Modalservice/ConfirmationDialog";
import { IdAndNameAndSelectDto } from "src/types";
import CoursePartListAndSelect from "src/coursepart/FindParts/CoursePartListAndSelect";
import { urlService } from "src/services/NavService";
import { ErrorService } from "src/components/Error/ErrorService";
import { AlertDialog, AlertOptions } from "src/components/Modalservice/AlertDialog";
import { CoursePartWithConnectedDTO } from "src/coursepart/domain/GetCoursepartsResponse";
import CoursePartFindByAuthor from "src/coursepart/FindParts/CoursePartFindByAuthor";


enum TabType {
    mine,
    others,
    byAuthor
}


const EditTheme = () => {

    const { courseId, themeId } = useParams();
    const [partsData] = useAtom(allPartsAtom);
    const debounce = useRef<debouncer | undefined>(undefined);
    const { languageService: t } = useI18n();
    const dialogPortal = useDialog();
    const navigate = useNavigate();
    const { state } = useLocation();

    const [theme, setTheme] = useState<TinyTheme | undefined>(undefined);

    const [tabType, setTabType] = useState(TabType.mine);

    useEffect(() => {
        if (themeId && courseId) {
            setTheme(courseService.getTheme(courseId, themeId));
        }
    }, [courseId, themeId]);

    useEffect(() => {
        debounce.current = new debouncer(saveData, 500);
        return () => debounce.current?.clear();
    }, [])


    const saveData = (name: string, id: string, currentCourseId: string) => {
        courseService.updateThemeName(currentCourseId, id, name);
    }

    const updateThemeName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.currentTarget.value;
        if (theme) {
            setTheme({ ...theme, Name: text });
            debounce.current && debounce.current.debounce(text, theme.Id, courseId);
        }
    }, [theme, courseId]);

    function deleteTheme(): void {

        dialogPortal({
            factory: (onSubmit, onCancel) => {
                const dprops: ConfirmationOptions = {
                    className: "",
                    title: t.getText("delete"),
                    message: <div className="preserve-white">{t.getText("delete_theme_confirm")}</div>,
                    languageService: t,
                    show: true,
                    onClose: onCancel,
                    onSubmit: onSubmit,
                    buttons: ConfirmationButtons.YesNo
                }
                return <ConfirmationDialog {...dprops} />
            },
            size: "md"
        }).then(async (res) => {
            if (res) {
                await courseService.deleteTheme(courseId!, themeId!);
                navigate(urlService.urlToCourse(courseId!));
            }
        });

    }

    function addNewPart(): void {
        dialogPortal({
            factory: (onSubmit, onCancel) => {
                const dprops: InputOptions = {
                    title: t.getText("courseparts.createnew"),
                    message: <div>{t.getText("mail.noname")}</div>,
                    languageService: t,
                    onCancel: onCancel,
                    onSubmit: onSubmit,
                    type: "input",
                    text: ""
                }
                return <InputDialog {...dprops} />
            },
            size: "md"
        }).then(async (text: string) => {
            try {
                const part = await courseService.addNewPart(text, courseId!, theme!.Id);
                if (!part) throw new Error("part not created");
            } catch (error) {
                console.error(error);
                ErrorService.setError({ message: t.getText("could.not.createpart"), header: t.getText("error"), when: new Date() });

            }
        });
    }

    const addPart = (partData: CoursePartWithConnectedDTO) => {

        const newPart: IdAndNameAndSelectDto =
            { Name: partData.Name, Id: partData.Id, Dirty: partData.Dirty || false }

        courseService.addPart(courseId!, theme!.Id, newPart);

    }

   
    const downloadLectures = async () => {
        const text = await courseService.downloadThemeLectures(courseId!, themeId!);
        if (text) {
            dialogPortal({
                factory: (onSubmit, onCancel) => {
                    const dprops: AlertOptions = {
                        title: t.getText("lectures"),
                        message: <div className="preserve-white">{text}</div>,
                        languageService: t,
                        onSubmit: onSubmit,
                        className: ""
                    }
                    return <AlertDialog {...dprops} />
                },
                size: "md"
            });
        }
    }

    return (<>
        <div className="mb-3">
            <h3 className="mb-4">{theme?.Name}</h3>
        </div >

        <div className="row">
            <div className="col-sm-12 col-md-8 position-relative">
                <div className="d-flex align-items-end">
                    <div className="form-group flex-grow-1">
                        <label>{t.getText("name")}</label>
                        <input disabled={state?.readOnly} type="text" className="form-control" value={theme?.Name} onChange={updateThemeName} />
                    </div>

                    {!state?.readOnly &&
                        <button onClick={deleteTheme} className="ml-5 btn btn-warning">{t.getText("delete")}</button>
                    }
                </div>

                {!state?.readOnly &&
                    <div className="form-group mt-5">

                        <div className="mt-3 mb-4">
                            <h3>{t.getText("lectures")}</h3>
                            <div className="d-flex align-items-center">
                                <div className="preserve-white mr-5">
                                    {t.getText("download.lectures")}
                                </div>
                                <div>
                                    <button onClick={downloadLectures} className="btn btn-small btn-primary">{t.getText("download")}</button>
                                </div>
                            </div>
                        </div>

                        <hr />

                        <h3 className="mt-4">{t.getText("courseparts")}</h3>
                        <div className="d-flex align-items-center">
                            <button onClick={addNewPart} className="my-3 btn btn-small btn-primary">{t.getText("courseparts.createnew")}</button>
                        </div>
                        <div className="my-3">
                            <strong>{t.getText("or.pick")}</strong>
                        </div>


                        <div className="custom-tab-heading-list" >
                            <button type="button" className={`${tabType === TabType.mine ? "active" : ""}`} onClick={() => setTabType(TabType.mine)}>{t.getText("your.courseparts")}</button>
                            <button type="button" className={`${tabType === TabType.others ? "active" : ""}`} onClick={() => setTabType(TabType.others)}>{t.getText("others")}</button>
                            <button type="button" className={`${tabType === TabType.byAuthor ? "active" : ""}`} onClick={() => setTabType(TabType.byAuthor)}>{t.getText("search.by.author")}</button>

                        </div>

                        {tabType === TabType.mine && (
                            <CoursePartListAndSelect items={partsData.OwnedParts} itemChosen={(i) => addPart(i)} />
                        )}

                        {tabType === TabType.others && (
                            <CoursePartListAndSelect items={partsData.EditParts} itemChosen={(i) => addPart(i)} />
                        )}

                        {tabType === TabType.byAuthor && (
                            <CoursePartFindByAuthor itemChosen={(i) => addPart(i)} />
                        )}



                    </div>
                }



            </div>
        </div>
    </>
    )
}

export default EditTheme;
